import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons";
import CommonToast from "../../../../components/_utils/CommonToast";
import { env } from "../../../../env";
import axiosInstance from "../../../globalInspector/axiosInstance";


const baseUrl = env.REACT_APP_BASE_URL;

const isSessionExpiredError = (error) => {
  const errors = error?.response?.data?.errors;
  return !!(errors?.token_expired || errors?.user_unauthorized);
};

const getFieldForceApiErrorMessage = (error) => {
  const errors = error?.response?.data?.errors;

  if (errors?.base?.[0]) return errors.base[0];
  if (errors?.token_expired?.[0]) return errors.token_expired[0];
  if (errors?.user_unauthorized?.[0]) return errors.user_unauthorized[0];

  if (errors && typeof errors === "object") {
    const firstKey = Object.keys(errors)[0];
    const firstValue = errors[firstKey];
    if (Array.isArray(firstValue) && firstValue[0]) return firstValue[0];
    if (typeof firstValue === "string") return firstValue;
  }

  return (
    error?.response?.data?.message ||
    error?.message ||
    "An unknown error occurred"
  );
};


export const createFieldForceAccount = createAsyncThunk(
  "createFieldForceAccount",
  async (values, { rejectWithValue }) => {
    const {
      bank_account,
      contact_no,
      email_id,
      gstin,
      ifsc_code,
      panNumber,
      category_ids,
      navigate,
      account_name,
      labels,
      recipient_email,
      amount,
      linked_user_id,
      userId,
      virtual,
    } = values;

    const requestBody = {
      field_force_accounts: {
        bank_account_no: bank_account,
        contact_no,
        email: email_id,
        gstin,
        ifsc: ifsc_code,
        pan: panNumber,
        name: account_name,
        labels,
        category_ids,
        recipient_email,
        field_force_custom_limit: amount,
        linked_user_id: linked_user_id || userId || null,
        virtual,
      },
    };

    try {
      const response = await axiosInstance.post(
        `${baseUrl}/accounts/field_force`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      const responseData = error.response?.data;
      if (responseData?.errors?.user_unauthorized) {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "FFUAccount" },
        });
        return rejectWithValue("User unauthorized");
      }

      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const editFieldForceAccount = createAsyncThunk(
  "editFieldForceAccount",

  async (values, { rejectWithValue }) => {
    const {
      id,
      pan,
      contact_no,
      email,
      gstin,
      category,
      category_ids,
      name,
      recipient_email,
      linked_user_id,
      field_force_custom_limit,
      bank_account_no,
      ifsc,
      navigate,
      virtual,
    } = values;

    try {
      const shouldUseBankUpdateEndpoint =
        bank_account_no !== undefined || ifsc !== undefined;
      const endpoint = shouldUseBankUpdateEndpoint
        ? `${baseUrl}/accounts/field_force/${id}`
        : `${baseUrl}/accounts/field_force/${id}/edit_other_details`;

      const response = await axiosInstance.put(
        endpoint,
        {
          field_force_accounts: {
            pan,
            email,
            contact_no,
            gstin,
            ...(category_ids !== undefined ? { category_ids } : { category }),
            name,
            virtual,
            recipient_email,
            ...(field_force_custom_limit !== undefined && {
              field_force_custom_limit,
            }),
            ...(linked_user_id !== undefined && {
              linked_user_id: linked_user_id || null,
            }),
            ...(bank_account_no !== undefined && {
              bank_account_no,
            }),
            ...(ifsc !== undefined && {
              ifsc,
            }),
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      const responseData = error.response?.data;
      let errorMessage = "Something went wrong";

      if (responseData?.errors?.user_unauthorized) {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "FFUAccount" },
        });
        return rejectWithValue("User unauthorized");
      }

      if (responseData?.errors) {
        const errs = responseData.errors;

        errorMessage =
          errs?.category?.[0] ||
          errs?.bank_account_number?.[0] ||
          errs?.[0]?.bank_account_number?.bank_account?.[0] ||
          errs?.[0]?.bank_account_number?.base?.[0] ||
          errs?.field_force_accounts?.[0] ||
          errs?.field_force_account?.[0] ||
          errs?.permanent_account_number?.[0] ||
          errs?.[0]?.gstin_detail?.base ||
          errs?.base?.[0] ||
          responseData?.message ||
          error.message;
      } else if (error.response) {
        errorMessage = `HTTP error! status: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = "No response received from the server";
      } else {
        errorMessage = error.message;
      }
      if (
        typeof errorMessage === "string" &&
        errorMessage
          .toLowerCase()
          .includes("already linked to the maximum limit")
      ) {
        errorMessage =
          "The selected account has reached the maximum linked user edit limit.";
      }
      CommonToast({
        text: errorMessage,
        icon: ICONS?.INFO_FILLED,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(errorMessage);
    }
  },
);


export const archiveFieldForceAccount = createAsyncThunk(
  "archiveFieldForceAccount",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/field_force/${id}/archive`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        CommonToast({
          text: getFieldForceApiErrorMessage(error),
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
      }

      return rejectWithValue(error?.response?.data || error);
    }
  },
);

export const unarchiveFieldForceAccount = createAsyncThunk(
  "unarchiveFieldForceAccount",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/field_force/${id}/unarchive`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        CommonToast({
          text: getFieldForceApiErrorMessage(error),
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
      }

      return rejectWithValue(error?.response?.data || error);
    }
  },
);

export const getAllFieldForceAccount = createAsyncThunk(
  "getAllFieldForceAccount",
  async (values, { rejectWithValue }) => {
    const {
      page_no,
      page_size,
      approval_status,
      start_date,
      end_date,
      bank_account_no,
      field_force_custom_limit,
      ifsc,
      pan,
      entity_id,
      category,
      category_id,
      category_ids,
      amount,
      search_string,
      labels,
      associate,
      showToast = true,
    } = values;
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/accounts/field_force`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
          params: {
            page_no: page_no ? page_no : undefined,
            page_size: page_size ? page_size : undefined,
            ...((approval_status || associate) && {
              approval_status: associate ? associate : Array.isArray(approval_status) ? [...approval_status] : [approval_status],
            }),
            start_date: start_date,
            end_date: end_date,
            bank_account_no: bank_account_no,
            field_force_custom_limit: field_force_custom_limit,
            ifsc: ifsc,
            pan: pan,
            entity_id: entity_id,
            category: category,
            category_id: category_id,
            category_ids: category_ids,
            search_string: search_string,
            amount: amount,
            labels: labels,
          },
        },
      );
      return response.data;
    } catch (error) {

      if (showToast && error?.response?.data?.errors?.base?.[0]) {
        CommonToast({
          text: error?.response?.data?.errors?.base[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,

          variant: "var(--error)",
          buttonNeeded: false,
          btnProps: {
            enabledBgColor: "var(--primary-bg)",
            enabledTextColor: "var(--text-primary)",
            btnText: "",
            handleFunction: () => {},
            icon: "",
            btnType: "button",
            isDisabled: false,
            hoverOption: "white",
          },
        });
      }

      if (showToast && error?.response?.data?.errors?.user_unauthorized?.[0]) {
        CommonToast({
          text: error.response.data.errors.user_unauthorized[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
          btnProps: {
            enabledBgColor: "var(--primary-bg)",
            enabledTextColor: "var(--text-primary)",
            btnText: "",
            handleFunction: () => {},
            icon: "",
            btnType: "button",
            isDisabled: false,
            hoverOption: "white",
          },
        });
      }

      return rejectWithValue(error);
    }
  },
);

export const addFieldForceAccNewLabel = createAsyncThunk(
  "addFieldForceAccNewLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    const data = {
      record_ids: record_ids,
      labels: labels,
    };

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/field_force/add_label`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );
      toast.dismiss();

      const existingLabels = JSON?.parse(
        sessionStorage?.getItem("fFAccAvailableLabels") || [],
      );

      const updatedLabels = Array?.from(
        new Set([...existingLabels, ...labels]),
      );

      const payload = JSON?.stringify(updatedLabels);
      sessionStorage?.setItem("fFAccAvailableLabels", payload);

      return response?.data;
    } catch (error) {
      if (error?.response?.data?.errors?.base?.[0]) {
        CommonToast({
          text: error?.response?.data?.errors?.base[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,

          variant: "var(--error)",
          buttonNeeded: false,
          btnProps: {
            enabledBgColor: "var(--primary-bg)",
            enabledTextColor: "var(--text-primary)",
            btnText: "",
            handleFunction: () => {},
            icon: "",
            btnType: "button",
            isDisabled: false,
            hoverOption: "white",
          },
        });
      }

      if (error?.response?.data?.errors?.user_unauthorized?.[0]) {
        CommonToast({
          text: error.response.data.errors.user_unauthorized[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
          btnProps: {
            enabledBgColor: "var(--primary-bg)",
            enabledTextColor: "var(--text-primary)",
            btnText: "",
            handleFunction: () => {},
            icon: "",
            btnType: "button",
            isDisabled: false,
            hoverOption: "white",
          },
        });
      }

      return rejectWithValue(error);
    }
  },
);

export const removeFieldForceAccNewLabel = createAsyncThunk(
  "removeFieldForceAccNewLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    const data = {
      record_ids: record_ids,
      labels: labels,
    };

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/field_force/remove_label`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response?.data;
    } catch (error) {
      if (error?.response?.data?.errors?.base) {
        CommonToast({
          text: error.response.data.errors.base[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,

          variant: "var(--error)",
          buttonNeeded: false,
          btnProps: {
            enabledBgColor: "var(--primary-bg)",
            enabledTextColor: "var(--text-primary)",
            btnText: "",
            handleFunction: () => {},
            icon: "",
            btnType: "button",
            isDisabled: false,
            hoverOption: "white",
          },
        });
      } else {
        CommonToast({
          text: error?.response?.data?.errors?.user_unauthorized[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,

          variant: "var(--error)",
          buttonNeeded: false,
          btnProps: {
            enabledBgColor: "var(--primary-bg)",
            enabledTextColor: "var(--text-primary)",
            btnText: "",
            handleFunction: () => {},
            icon: "",
            btnType: "button",
            isDisabled: false,
            hoverOption: "white",
          },
        });
      }

      return rejectWithValue(error);
    }
  },
);

export const getSpecificFieldForceAccount = createAsyncThunk(
  "getSpecificFieldForceAccount",
  async (payload, { rejectWithValue }) => {
    const { id } = payload;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/accounts/field_force/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        CommonToast({
          text: getFieldForceApiErrorMessage(error),
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
      }

      return rejectWithValue(error);
    }
  },
);

export const filedForceRequiredApproval = createAsyncThunk(
  "filedForceRequiredApproval",
  async (values) => {
    const {
      page_no,
      page_size,
      category_ids,
      search_string,
      bank_account_no,
      ifsc,
      start_date,
      end_date,
      pan,
      labels,
      recipient_email,
      entity_id,
    } = values;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/accounts/field_force`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
          params: {
            approval_status: ["pending"],
            page_no: page_no,
            page_size: page_size,
            category_ids: category_ids,
            search_string: search_string,
            start_date,
            end_date,
            bank_account_no: bank_account_no,
            ifsc: ifsc,
            pan: pan,
            labels: labels,
            recipient_email: recipient_email,
            entity_id: entity_id,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
);

export const filedForceApproval = createAsyncThunk(
  "filedForceApproval",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/field_force/${id}/approve`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return rejectWithValue(undefined);
      }

      const errData = error?.response?.data || {
        status: false,
        message: "Something went wrong",
      };

      return rejectWithValue(errData);
    }
  },
);

export const filedForceDeniedAccount = createAsyncThunk(
  "filedForceDeniedAccount",
  async ({ id, AccountIdData }, { rejectWithValue }) => {
    try {
      const data = {
        rejection_reason: AccountIdData,
      };

      const response = await axiosInstance.put(
        `${baseUrl}/accounts/field_force/${id}/reject`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
          validateStatus: () => true,
        },
      );

      if (response.status === 404) {
        return undefined;
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || null);
    }
  },
);

const fieldForceAccount = createSlice({
  name: "fieldForceAccount",
  initialState: {
    toggleFfa: false,
    storedPage: 1,
    fFAccAvailableLabels: [],

    allfieldForceAccountData: [],
    allfieldForceAccountLoading: false,
    allfieldForceAccountError: false,

    addFfAccLabelsLoading: true,
    addFfAccLabelsData: [],
    addFfAccLabelsError: false,

    removeFfAccLabelsLoading: true,
    removeFfAccLabelsData: [],
    removeFfAccLabelsError: false,

    createFieldForceAccountData: [],
    createFieldForceAccountLoading: null,
    createFieldForceAccountError: null,

    fieldforceRequiredApprovalData: null,
    fieldforceRequiredApprovalLoading: null,
    fieldforceRequiredApprovalError: null,

    fieldforceWaitingApprovalData: null,
    fieldforceWaitingApprovalLoading: null,
    fieldforceWaitingApprovalError: null,

    fieldforceDeniedApprovalData: null,
    fieldforceDeniedApprovalLoading: null,
    fieldforceDeniedApprovalError: null,

    getSpecificFieldForceAccountLoading: null,
    getSpecificFieldForceAccountError: null,
    getSpecificFieldForceAccountData: null,

    editFieldForceAccountLoading: false,
    editFieldForceAccountError: null,
    editFieldForceAccountData: null,

    archiveFieldForceAccountloading: false,
    archiveFieldForceAccountError: null,
    archiveFieldForceAccountData: null,
  },
  reducers: {
    setToggleFfa: (state, action) => {
      state.toggleFfa = action.payload;
    },
    resetToggleFfa: (state) => {
      state.toggleFfa = false;
    },
    resetpage: (state) => {
      state.storedPage = 1;
    },
    resetEditFieldForceAccountData: (state) => {
      state.editFieldForceAccountData = null;
    },
    setStoredPage: (state, action) => {
      state.storedPage = action.payload;
    },
    setFFAccAvailableLabels: (state, action) => {
      state.fFAccAvailableLabels = action.payload;
    },
    resetFFAccLabelLoading: (state) => {
      state.addFfAccLabelsLoading = false;
      state.removeFfAccLabelsLoading = false;
    },

    resetEditFieldForceAccount: (state) => {
      state.editFieldForceAccountLoading = false;
      state.editFieldForceAccountError = null;
      state.editFieldForceAccountData = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllFieldForceAccount.pending, (state, action) => {
      state.allfieldForceAccountLoading = true;
      if (action?.payload?.summary?.available_labels) {
        state.fFAccAvailableLabels = [];
      }
    });
    builder.addCase(getAllFieldForceAccount.fulfilled, (state, action) => {
      state.allfieldForceAccountLoading = false;
      state.allfieldForceAccountData = action.payload;
      state.fFAccAvailableLabels = action?.payload?.summary?.available_labels;
      if (action?.payload?.summary?.available_labels) {
        sessionStorage.setItem(
          "fFAccAvailableLabels",
          JSON.stringify(action?.payload?.summary?.available_labels || []),
        );
      }
    });
    builder.addCase(getAllFieldForceAccount.rejected, (state, action) => {
      state.fieldforceRequiredApprovalError = true;
      state.fieldforceRequiredApprovalLoading = false;
      state.fieldforceRequiredApprovalData = null;
      if (action?.payload?.summary?.available_labels) {
        state.fFAccAvailableLabels = [];
      }
    });

    builder.addCase(addFieldForceAccNewLabel.pending, (state, action) => {
      state.addFfAccLabelsLoading = true;
    });
    builder.addCase(addFieldForceAccNewLabel.fulfilled, (state, action) => {
      state.addFfAccLabelsLoading = false;
      state.addFfAccLabelsData = action.payload;
    });
    builder.addCase(addFieldForceAccNewLabel.rejected, (state, action) => {
      state.addFfAccLabelsLoading = false;
      state.addFfAccLabelsError = action.payload;
      state.addFfAccLabelsData = null;
    });
    builder.addCase(removeFieldForceAccNewLabel.pending, (state, action) => {
      state.removeFfAccLabelsLoading = true;
    });
    builder.addCase(removeFieldForceAccNewLabel.fulfilled, (state, action) => {
      state.removeFfAccLabelsLoading = false;
      state.removeFfAccLabelsData = action.payload;
    });
    builder.addCase(removeFieldForceAccNewLabel.rejected, (state, action) => {
      state.removeFfAccLabelsLoading = false;
      state.removeFfAccLabelsError = action.payload;
      state.removeFfAccLabelsData = null;
    });

    builder.addCase(filedForceRequiredApproval.pending, (state, action) => {
      state.fieldforceRequiredApprovalLoading = true;
    });
    builder.addCase(filedForceRequiredApproval.fulfilled, (state, action) => {
      state.fieldforceRequiredApprovalLoading = false;
      state.fieldforceRequiredApprovalData = action.payload;
    });
    builder.addCase(filedForceRequiredApproval.rejected, (state, action) => {
      state.fieldforceRequiredApprovalError = true;
      state.fieldforceRequiredApprovalData = null;
    });

    builder.addCase(filedForceDeniedAccount.pending, (state, action) => {
      state.fieldforceDeniedApprovalLoading = true;
    });
    builder.addCase(filedForceDeniedAccount.fulfilled, (state, action) => {
      state.fieldforceDeniedApprovalLoading = false;
      state.fieldforceDeniedApprovalData = action.payload;
    });
    builder.addCase(filedForceDeniedAccount.rejected, (state, action) => {
      state.fieldforceDeniedApprovalError = true;
      state.fieldforceDeniedApprovalData = null;
      state.fieldforceDeniedApprovalLoading = false;
    });

    builder.addCase(filedForceApproval.pending, (state, action) => {
      state.fieldforceWaitingApprovalLoading = true;
    });
    builder.addCase(filedForceApproval.fulfilled, (state, action) => {
      state.fieldforceWaitingApprovalLoading = false;
      state.fieldforceWaitingApprovalData = action.payload;
    });
    builder.addCase(filedForceApproval.rejected, (state, action) => {
      state.fieldforceWaitingApprovalError = true;
      state.fieldforceWaitingApprovalData = null;
      state.fieldforceWaitingApprovalLoading = false;
    });
    builder.addCase(createFieldForceAccount.pending, (state) => {
      state.createFieldForceAccountLoading = true;
    });
    builder.addCase(createFieldForceAccount.fulfilled, (state, action) => {
      state.createFieldForceAccountLoading = false;
      state.createFieldForceAccountData = action.payload;
    });
    builder.addCase(createFieldForceAccount.rejected, (state) => {
      state.createFieldForceAccountError = true;
      state.createFieldForceAccountLoading = false;
    });

    builder.addCase(getSpecificFieldForceAccount.pending, (state) => {
      state.getSpecificFieldForceAccountLoading = true;
      state.getSpecificFieldForceAccountError = false;
    });
    builder.addCase(getSpecificFieldForceAccount.fulfilled, (state, action) => {
      state.getSpecificFieldForceAccountLoading = false;
      state.getSpecificFieldForceAccountData = action.payload;
    });
    builder.addCase(getSpecificFieldForceAccount.rejected, (state) => {
      state.getSpecificFieldForceAccountError = true;
      state.getSpecificFieldForceAccountLoading = false;
    });

    builder.addCase(editFieldForceAccount.pending, (state) => {
      state.editFieldForceAccountLoading = true;
      state.editFieldForceAccountError = null;
    });
    builder.addCase(editFieldForceAccount.fulfilled, (state, action) => {
      state.editFieldForceAccountLoading = false;
      state.editFieldForceAccountData = action.payload;
    });
    builder.addCase(editFieldForceAccount.rejected, (state, action) => {
      state.editFieldForceAccountLoading = false;
      state.editFieldForceAccountError = action.payload || true;
    });

    builder.addCase(archiveFieldForceAccount.pending, (state) => {
      state.archiveFieldForceAccountloading = true;
      state.archiveFieldForceAccountError = null;
    });
    builder.addCase(archiveFieldForceAccount.fulfilled, (state, action) => {
      state.archiveFieldForceAccountloading = false;
      state.archiveFieldForceAccountData = action.payload;
    });
    builder.addCase(archiveFieldForceAccount.rejected, (state, action) => {
      state.archiveFieldForceAccountloading = false;
      state.archiveFieldForceAccountError = action.payload || true;
    });
  },
});

export const {
  setStoredPage,
  setToggleFfa,
  resetToggleFfa,
  setFFAccAvailableLabels,
  resetFFAccLabelLoading,
  resetEditFieldForceAccount,
  resetEditFieldForceAccountData,
} = fieldForceAccount.actions;

export default fieldForceAccount.reducer;
